import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../lib/apiBase";

const CAPABILITIES = {
  TOURNAMENT_DISPUTES: "tournament_disputes",
};

function getToken() {
  return (
    localStorage.getItem("ga_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    ""
  );
}

async function request(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    method: options.method || "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
    cache: "no-store",
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

function money(cents) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(cents || 0) / 100);
}

function dateTime(value) {
  if (!value) return "TBD";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "TBD" : date.toLocaleString();
}

function titleCase(value) {
  return String(value || "unknown")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function isCrewHeadquartersTournament(tournament) {
  return (
    String(tournament?.id || "").toLowerCase() === "gas-up-3v3-crew-hq-2026" ||
    String(tournament?.slug || "").toLowerCase() === "gas-up-3v3-crew-headquarters"
  );
}

function tournamentDateLabel(tournament) {
  if (tournament?.scheduledAt) return dateTime(tournament.scheduledAt);
  if (isCrewHeadquartersTournament(tournament)) return "TBD — set after the 16-team bracket fills";
  return "TBD";
}

function tournamentFormatLabel(tournament) {
  const parts = [tournament?.game, tournament?.mode].filter(Boolean);
  if (tournament?.maxOverall) parts.push(`Max ${tournament.maxOverall} OVR`);
  else if (isCrewHeadquartersTournament(tournament)) parts.push("No OVR cap");
  if (tournament?.seriesBestOf) parts.push(`Best of ${tournament.seriesBestOf}`);
  return parts.join(" • ");
}

function Metric({ label, value, detail, tone = "red" }) {
  return (
    <article className={`gao-metric gao-tone-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function OperationCard({ to, eyebrow, title, body, stat, disabled = false }) {
  const content = (
    <>
      <div className="gao-operation-top">
        <span>{eyebrow}</span>
        <b>{disabled ? "LOCKED" : "OPEN →"}</b>
      </div>
      <h3>{title}</h3>
      <p>{body}</p>
      {stat ? <div className="gao-operation-stat">{stat}</div> : null}
    </>
  );
  if (disabled) return <article className="gao-operation gao-operation-disabled">{content}</article>;
  return <Link className="gao-operation" to={to}>{content}</Link>;
}

function StatusPill({ children, tone = "neutral" }) {
  return <span className={`gao-pill gao-pill-${tone}`}>{children}</span>;
}

export default function AdminOperations() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [me, setMe] = useState(null);
  const [founder, setFounder] = useState(null);
  const [sponsors, setSponsors] = useState(null);
  const [questions, setQuestions] = useState(null);
  const [tournaments, setTournaments] = useState([]);
  const [tournamentStatusById, setTournamentStatusById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const myWorkspace = await request("/admin-operations/me");
      setMe(myWorkspace);

      const tournamentListResult = await Promise.resolve(request("/tournaments")).catch(() => null);
      const list = Array.isArray(tournamentListResult?.tournaments) ? tournamentListResult.tournaments : [];
      setTournaments(list);

      const statusJobs = list.map(async (tournament) => {
        try {
          const status = await request(`/tournaments/${encodeURIComponent(tournament.id)}/me`);
          return [tournament.id, status];
        } catch {
          return [tournament.id, null];
        }
      });

      const jobs = [
        Promise.all(statusJobs),
        myWorkspace.isFounder ? request("/admin-operations/founder") : Promise.resolve(null),
        myWorkspace.isFounder ? request("/sponsor-inbox") : Promise.resolve(null),
        myWorkspace.isFounder ? request("/tournaments/questions/admin/list") : Promise.resolve(null),
      ];

      const [statusResult, founderResult, sponsorResult, questionResult] = await Promise.allSettled(jobs);
      if (statusResult.status === "fulfilled") {
        setTournamentStatusById(Object.fromEntries(statusResult.value));
      } else {
        setTournamentStatusById({});
      }
      setFounder(founderResult.status === "fulfilled" ? founderResult.value : null);
      setSponsors(sponsorResult.status === "fulfilled" ? sponsorResult.value : null);
      setQuestions(questionResult.status === "fulfilled" ? questionResult.value : null);
    } catch (err) {
      setMe(null);
      setError(err.message || "Could not load the GAStreaming operations portal.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isFounder = Boolean(me?.isFounder);
  const promotion = me?.promotion || {};
  const totals = promotion?.totals || {};
  const canReviewDisputes = Boolean(me?.access?.capabilities?.includes(CAPABILITIES.TOURNAMENT_DISPUTES));
  const founderParticipants = founder?.participants || [];
  const limitedAdmins = founderParticipants.filter((summary) => !summary?.access?.isFounder);
  const activeAdmins = limitedAdmins.filter((summary) => summary?.participant?.status !== "revoked").length;
  const globalUnpaidCents = founderParticipants.reduce((sum, summary) => sum + Number(summary?.totals?.unpaidCents || 0), 0);
  const pendingReferralCount = founderParticipants.reduce((sum, summary) => sum + Number(summary?.totals?.pendingSignups || 0), 0);
  const questionCount = Number(
    questions?.counts?.new ||
    (questions?.questions || []).filter((question) => question.status === "new").length ||
    0
  );
  const totalApprovedTeams = tournaments.reduce((sum, tournament) => sum + Number(tournament?.approvedTeamCount || 0), 0);
  const totalTeamSlots = tournaments.reduce((sum, tournament) => sum + Number(tournament?.maxTeams || 0), 0);
  const openDisputes = Object.values(tournamentStatusById).reduce(
    (sum, status) => sum + Number(status?.adminDisputeCount || 0),
    0
  );
  const pendingWinnerPayouts = Object.values(tournamentStatusById).reduce(
    (sum, status) => sum + Number(status?.adminPayoutCount || 0),
    0
  );

  const operations = useMemo(() => {
    if (isFounder) {
      return [
        {
          to: "/admin/promotion",
          eyebrow: "PEOPLE + PAYOUTS",
          title: "Admin Team & Invites",
          body: "Invite admins, approve referrals, verify tournament bonuses, and record paid rewards.",
          stat: `${pendingReferralCount} referrals need review`,
        },
        {
          to: "/admin/sponsors",
          eyebrow: "FOUNDER ONLY",
          title: "Sponsor Inbox",
          body: "Read and reply to sponsor emails using the official GAStreaming address.",
          stat: `${Number(sponsors?.unread || 0)} unread messages`,
        },
        {
          to: "/tournaments",
          eyebrow: "FULL CONTROL",
          title: "Tournament Operations",
          body: "Registration, team approvals, schedules, brackets, communications, live feeds, and match control for every GA tournament.",
          stat: `${tournaments.length} events • ${totalApprovedTeams}/${totalTeamSlots || 0} combined slots filled`,
        },
        {
          to: "/admin/tournament-reviews",
          eyebrow: "EVIDENCE DESK",
          title: "Tournament Disputes",
          body: "Review screenshots, apply official scores, or return a match to play.",
          stat: `${openDisputes} open disputes`,
        },
        {
          to: "/admin/tournament-payouts",
          eyebrow: "FOUNDER ONLY",
          title: "Winner Payout Safety",
          body: "Verify all safety checks before any protected winner payout is released.",
          stat: `${pendingWinnerPayouts} winner payouts pending`,
        },
        {
          to: "/admin/digital-venue",
          eyebrow: "EVENT CONTROL",
          title: "Digital Venue",
          body: "Create the exclusive event, track tickets, check people in, and monitor access.",
          stat: "Founder event workspace",
        },
        {
          to: "/admin/questions",
          eyebrow: "COMMUNITY SUPPORT",
          title: "Platform Questions",
          body: "Answer tournament, account, collaboration, and business questions.",
          stat: `${questionCount} new questions`,
        },
        {
          to: "/console-streaming",
          eyebrow: "STREAMER ACCESS",
          title: "Console Streaming Crew",
          body: "Manage PS5/Xbox direct-stream access, Twitch source links, normal GA channels, and OBS/Mux readiness for approved crew.",
          stat: "GetActive • Slim • Dq • Lost • Debacle • GASxJay2",
        },
        {
          to: "/admin/notifications",
          eyebrow: "PLATFORM COMMS",
          title: "Notifications",
          body: "Manage the founder notification workflow and important platform messages.",
          stat: "Owner controls",
        },
      ];
    }

    return [
      {
        to: "/admin/promotion",
        eyebrow: "MY WORKSPACE",
        title: "Invite Earnings",
        body: "Open your referral link, earnings history, Stripe payout status, and program terms.",
        stat: `${money(totals.unpaidCents)} pending payout`,
      },
      {
        to: "/admin/tournament-reviews",
        eyebrow: "ASSIGNED ACCESS",
        title: "Tournament Disputes",
        body: "Review match evidence and resolve disputes assigned to the limited-admin team.",
        stat: canReviewDisputes ? `${openDisputes} open disputes` : "Founder has not granted this permission",
        disabled: !canReviewDisputes,
      },
      {
        to: "/tournaments",
        eyebrow: "TOURNAMENT HUB",
        title: "Tournament Status",
        body: "Follow every active tournament, approved teams, schedule, bracket, and current status.",
        stat: `${tournaments.length} tournaments available`,
      },
    ];
  }, [
    canReviewDisputes,
    isFounder,
    openDisputes,
    pendingReferralCount,
    pendingWinnerPayouts,
    questionCount,
    sponsors?.unread,
    totalApprovedTeams,
    totalTeamSlots,
    totals.unpaidCents,
    tournaments.length,
  ]);

  async function copyReferral() {
    try {
      await navigator.clipboard.writeText(me?.shareUrl || "");
      setNotice("Your personal referral link was copied.");
    } catch {
      window.prompt("Copy your personal referral link:", me?.shareUrl || "");
    }
  }

  async function setDisputeAccess(summary, enabled) {
    const participantId = summary?.participant?.id;
    if (!participantId) return;
    setBusy(participantId);
    setError("");
    setNotice("");
    try {
      await request(`/admin-operations/founder/admins/${encodeURIComponent(participantId)}/access`, {
        method: "PATCH",
        body: { tournamentDisputes: enabled },
      });
      setNotice(`${summary?.participant?.username || summary?.participant?.email}'s dispute-desk access was ${enabled ? "enabled" : "removed"}.`);
      const refreshed = await request("/admin-operations/founder");
      setFounder(refreshed);
    } catch (err) {
      setError(err.message || "Could not update that admin's permissions.");
    } finally {
      setBusy("");
    }
  }

  if (loading) {
    return <main className="gao-page"><div className="gao-loading">Opening the private operations portal…</div></main>;
  }

  if (!me) {
    return (
      <main className="gao-page">
        <section className="gao-denied">
          <div className="gao-eyebrow">PRIVATE GA OPERATIONS</div>
          <h1>Admin access required</h1>
          <p>{error || "This page requires a founder-issued admin invitation."}</p>
          <Link to="/">Return to GAStreaming</Link>
        </section>
      </main>
    );
  }

  return (
    <main className="gao-page">
      <style>{`
        .gao-page{width:min(1320px,calc(100% - 28px));margin:0 auto 80px;color:#fff}.gao-page *{box-sizing:border-box}.gao-hero{position:relative;overflow:hidden;border:1px solid rgba(255,78,92,.3);border-radius:28px;padding:clamp(24px,5vw,52px);background:radial-gradient(circle at 85% 0,rgba(229,35,51,.24),transparent 37%),linear-gradient(145deg,rgba(56,7,12,.95),rgba(6,6,9,.97));box-shadow:0 30px 90px rgba(0,0,0,.4)}.gao-hero-grid{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:24px;align-items:start}.gao-eyebrow{color:#ff9aa2;font-size:12px;font-weight:1000;letter-spacing:.17em}.gao-hero h1,.gao-denied h1{margin:8px 0 10px;font-size:clamp(2.3rem,7vw,5.6rem);line-height:.95}.gao-hero p,.gao-denied p{margin:0;color:rgba(255,255,255,.72);font-size:clamp(1rem,2vw,1.18rem);line-height:1.6;max-width:820px}.gao-role{border:1px solid rgba(255,255,255,.17);border-radius:999px;padding:10px 14px;background:rgba(255,255,255,.07);font-size:12px;font-weight:1000;white-space:nowrap}.gao-warning{margin-top:22px;padding:13px 15px;border-radius:14px;border:1px solid rgba(255,192,71,.27);background:rgba(118,69,0,.21);color:#ffd995;font-size:13px;line-height:1.55}.gao-metrics{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px;margin-top:16px}.gao-metric{display:grid;gap:7px;padding:17px;border-radius:17px;border:1px solid rgba(255,255,255,.1);background:rgba(5,5,8,.72)}.gao-metric span{color:rgba(255,255,255,.55);font-size:11px;font-weight:1000;letter-spacing:.1em;text-transform:uppercase}.gao-metric strong{font-size:clamp(1.45rem,3vw,2.25rem)}.gao-metric small{color:rgba(255,255,255,.53);line-height:1.4}.gao-tone-green{border-color:rgba(54,222,126,.25)}.gao-tone-gold{border-color:rgba(255,195,60,.28)}.gao-section{margin-top:20px;border:1px solid rgba(255,255,255,.1);border-radius:23px;padding:clamp(17px,3vw,28px);background:rgba(7,7,10,.84)}.gao-section-head{display:flex;justify-content:space-between;align-items:end;gap:16px;flex-wrap:wrap;margin-bottom:17px}.gao-section h2{margin:5px 0 0;font-size:clamp(1.7rem,4vw,3rem)}.gao-section-copy{color:rgba(255,255,255,.56);font-size:13px;line-height:1.5;max-width:650px}.gao-refresh,.gao-copy,.gao-toggle{border:1px solid rgba(255,255,255,.17);border-radius:999px;background:rgba(255,255,255,.07);color:#fff;padding:10px 14px;font-weight:900;cursor:pointer}.gao-operations{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:12px}.gao-operation{display:block;min-height:220px;padding:19px;border:1px solid rgba(255,69,82,.22);border-radius:18px;background:linear-gradient(145deg,rgba(48,9,13,.74),rgba(7,7,10,.93));text-decoration:none;color:#fff;transition:transform .18s ease,border-color .18s ease}.gao-operation:hover{transform:translateY(-3px);border-color:rgba(255,88,101,.64)}.gao-operation-disabled{opacity:.48;cursor:not-allowed}.gao-operation-disabled:hover{transform:none}.gao-operation-top{display:flex;justify-content:space-between;gap:10px;color:#ff9aa2;font-size:10px;font-weight:1000;letter-spacing:.1em}.gao-operation-top b{color:rgba(255,255,255,.45)}.gao-operation h3{margin:17px 0 8px;font-size:1.35rem}.gao-operation p{margin:0;color:rgba(255,255,255,.62);line-height:1.52;font-size:13px}.gao-operation-stat{margin-top:18px;color:#fff;font-weight:900;font-size:13px}.gao-admin-list{display:grid;gap:11px}.gao-admin{display:grid;grid-template-columns:minmax(190px,1.3fr) repeat(3,minmax(120px,.7fr)) minmax(190px,1fr);gap:14px;align-items:center;padding:16px;border-radius:16px;border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.025)}.gao-admin-name{font-weight:1000;font-size:17px}.gao-muted{margin-top:4px;color:rgba(255,255,255,.47);font-size:12px;word-break:break-word}.gao-admin-stat{display:grid;gap:4px}.gao-admin-stat span{color:rgba(255,255,255,.45);font-size:10px;font-weight:1000;letter-spacing:.09em}.gao-admin-stat strong{font-size:15px}.gao-permission{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:11px;border-radius:13px;background:rgba(0,0,0,.3)}.gao-toggle[aria-pressed='true']{background:rgba(20,137,72,.35);border-color:rgba(76,237,143,.35);color:#9effc6}.gao-toggle:disabled{opacity:.5;cursor:wait}.gao-sponsors{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px}.gao-email{display:block;color:#fff;text-decoration:none;padding:17px;border-radius:16px;border:1px solid rgba(255,255,255,.1);background:rgba(255,255,255,.03)}.gao-email:hover{border-color:rgba(255,96,108,.48)}.gao-email-from{color:#ff9aa2;font-weight:900;font-size:12px}.gao-email h3{margin:8px 0;font-size:1.05rem}.gao-email p{margin:0;color:rgba(255,255,255,.55);font-size:12px;line-height:1.5}.gao-email time{display:block;margin-top:11px;color:rgba(255,255,255,.38);font-size:11px}.gao-referral{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:center;padding:14px;border-radius:14px;background:#08080b;border:1px solid rgba(255,255,255,.11)}.gao-referral code{color:#ffb4ba;word-break:break-all}.gao-recent{display:grid;gap:9px;margin-top:14px}.gao-recent-item{display:flex;justify-content:space-between;align-items:center;gap:14px;padding:12px;border-radius:13px;background:rgba(255,255,255,.035)}.gao-pill{display:inline-flex;border-radius:999px;padding:6px 9px;font-size:10px;font-weight:1000;letter-spacing:.05em}.gao-pill-green{background:rgba(24,139,75,.26);color:#91ffc0}.gao-pill-gold{background:rgba(142,88,3,.3);color:#ffd586}.gao-pill-neutral{background:rgba(255,255,255,.08);color:rgba(255,255,255,.72)}.gao-tournament-list{display:grid;gap:12px}.gao-tournament{display:grid;grid-template-columns:1.5fr repeat(3,minmax(120px,.65fr));gap:14px;align-items:center;padding:17px;border-radius:17px;border:1px solid rgba(255,255,255,.09);background:rgba(255,255,255,.025)}.gao-tournament-hq{border-color:rgba(125,74,255,.38);background:linear-gradient(135deg,rgba(52,20,90,.28),rgba(5,13,20,.72))}.gao-tournament h3{margin:6px 0;font-size:1.5rem}.gao-event-note{margin-top:8px;color:#c5b5ff;font-size:12px;font-weight:800;line-height:1.45}.gao-event-actions{grid-column:1/-1;display:flex;flex-wrap:wrap;gap:8px;padding-top:3px}.gao-event-action{display:inline-flex;align-items:center;justify-content:center;text-decoration:none;color:#fff;border:1px solid rgba(255,255,255,.16);border-radius:999px;padding:8px 11px;background:rgba(255,255,255,.06);font-size:11px;font-weight:1000;letter-spacing:.04em}.gao-event-action:hover{border-color:rgba(132,92,255,.58);background:rgba(105,55,190,.18)}.gao-alert{margin-top:14px;padding:13px 15px;border-radius:14px;font-weight:800}.gao-alert-error{background:rgba(130,13,22,.3);border:1px solid rgba(255,76,88,.3);color:#ffb7b7}.gao-alert-success{background:rgba(17,106,49,.25);border:1px solid rgba(61,203,106,.28);color:#a8f3bf}.gao-loading,.gao-denied{margin:40px auto;padding:36px;max-width:760px;border-radius:24px;border:1px solid rgba(255,80,94,.28);background:rgba(20,6,9,.9);text-align:center}.gao-denied a{display:inline-block;margin-top:18px;color:#fff;font-weight:900}.gao-empty{padding:18px;border:1px dashed rgba(255,255,255,.14);border-radius:14px;color:rgba(255,255,255,.5);text-align:center}@media(max-width:1050px){.gao-operations{grid-template-columns:repeat(2,minmax(0,1fr))}.gao-admin{grid-template-columns:1fr 1fr}.gao-permission{grid-column:1/-1}.gao-sponsors{grid-template-columns:1fr}.gao-metrics{grid-template-columns:repeat(2,minmax(0,1fr))}}@media(max-width:650px){.gao-hero-grid{grid-template-columns:1fr}.gao-role{justify-self:start}.gao-metrics,.gao-operations{grid-template-columns:1fr}.gao-admin{grid-template-columns:1fr}.gao-tournament{grid-template-columns:1fr 1fr}.gao-tournament>div:first-child{grid-column:1/-1}.gao-referral{grid-template-columns:1fr}.gao-copy{justify-self:start}}
      `}</style>

      <section className="gao-hero">
        <div className="gao-hero-grid">
          <div>
            <div className="gao-eyebrow">GASTREAMING • PRIVATE OPERATIONS</div>
            <h1>{isFounder ? "Founder Command" : "My Admin Workspace"}</h1>
            <p>
              {isFounder
                ? "One place to oversee your people, sponsor relationships, tournaments, event access, and money obligations."
                : "Your assigned tools, invite earnings, pending payout, and tournament responsibilities—nothing outside the access Founder grants you."}
            </p>
          </div>
          <div className="gao-role">{me?.access?.roleLabel}</div>
        </div>
        {isFounder ? (
          <div className="gao-warning">
            <strong>Money control:</strong> pending amounts are obligations, not proof that cash has moved. Sponsor mail, payout execution, and global financial details stay Founder-only. Recording an invite payout never sends money automatically.
          </div>
        ) : null}
      </section>

      {error ? <div className="gao-alert gao-alert-error">{error}</div> : null}
      {notice ? <div className="gao-alert gao-alert-success">{notice}</div> : null}

      <section className="gao-metrics">
        {isFounder ? (
          <>
            <Metric label="Unpaid admin rewards" value={money(globalUnpaidCents)} detail="Approved, not recorded paid" tone="gold" />
            <Metric label="Limited admins" value={activeAdmins} detail="Private workspaces under Founder" />
            <Metric label="Sponsor unread" value={Number(sponsors?.unread || 0)} detail="Founder-only inbox" tone="green" />
            <Metric label="Open disputes" value={openDisputes} detail="Across all tournament evidence queues" />
          </>
        ) : (
          <>
            <Metric label="My pending payout" value={money(totals.unpaidCents)} detail="Approved and not yet recorded paid" tone="gold" />
            <Metric label="Approved invites" value={Number(totals.approvedSignups || 0)} detail={`${Number(totals.remainingPaidSignupSlots || 0)} paid slots remain`} tone="green" />
            <Metric label="Awaiting review" value={Number(totals.pendingSignups || 0)} detail="Founder must approve" />
            <Metric label="Open disputes" value={openDisputes} detail={canReviewDisputes ? "Assigned evidence queues" : "No dispute permission"} />
          </>
        )}
      </section>

      <section className="gao-section">
        <div className="gao-section-head">
          <div>
            <div className="gao-eyebrow">OPERATIONS DIRECTORY</div>
            <h2>{isFounder ? "Everything under one roof" : "Your assigned controls"}</h2>
          </div>
          <button className="gao-refresh" type="button" onClick={load}>Refresh data</button>
        </div>
        <div className="gao-operations">
          {operations.map((operation) => <OperationCard key={operation.title} {...operation} />)}
        </div>
      </section>

      {!isFounder ? (
        <section className="gao-section">
          <div className="gao-section-head">
            <div>
              <div className="gao-eyebrow">MY INVITE PROGRAM</div>
              <h2>Referral link & earnings</h2>
            </div>
          </div>
          <div className="gao-referral">
            <code>{me?.shareUrl || "Referral link is not ready."}</code>
            <button type="button" className="gao-copy" onClick={copyReferral}>Copy link</button>
          </div>
          <div className="gao-recent">
            {(promotion?.referrals || []).slice(0, 6).map((referral) => (
              <div className="gao-recent-item" key={referral.id}>
                <div>
                  <strong>@{referral.referredUsername || "new member"}</strong>
                  <div className="gao-muted">Submitted {dateTime(referral.createdAt)}</div>
                </div>
                <StatusPill tone={referral.status === "approved" || referral.status === "paid" ? "green" : referral.status === "pending_review" ? "gold" : "neutral"}>
                  {titleCase(referral.status)} • {money(referral.amountCents)}
                </StatusPill>
              </div>
            ))}
            {!promotion?.referrals?.length ? <div className="gao-empty">No invite earnings have been submitted yet.</div> : null}
          </div>
        </section>
      ) : null}

      {isFounder ? (
        <section className="gao-section">
          <div className="gao-section-head">
            <div>
              <div className="gao-eyebrow">ADMIN OVERSIGHT</div>
              <h2>Your limited admin team</h2>
            </div>
            <div className="gao-section-copy">Each person sees only their earnings plus the permissions shown here. Sponsor mail and payout controls remain Founder-only.</div>
          </div>
          <div className="gao-admin-list">
            {limitedAdmins.map((summary) => {
              const hasDisputes = summary?.access?.capabilities?.includes(CAPABILITIES.TOURNAMENT_DISPUTES);
              const payoutReady = Boolean(summary?.user?.stripeConnect?.payoutsEnabled && summary?.user?.stripeConnect?.detailsSubmitted);
              return (
                <article className="gao-admin" key={summary.participant.id}>
                  <div>
                    <div className="gao-admin-name">{summary.participant.username || "Unnamed admin"}</div>
                    <div className="gao-muted">{summary.participant.email}</div>
                    <div className="gao-muted">{titleCase(summary.participant.status)} • {summary.access.roleLabel}</div>
                  </div>
                  <div className="gao-admin-stat"><span>UNPAID</span><strong>{money(summary.totals.unpaidCents)}</strong></div>
                  <div className="gao-admin-stat"><span>INVITES</span><strong>{summary.totals.approvedSignups} approved</strong></div>
                  <div className="gao-admin-stat"><span>PAYOUT ACCOUNT</span><strong>{payoutReady ? "Ready" : "Needs setup"}</strong></div>
                  <div className="gao-permission">
                    <div><strong>Dispute desk</strong><div className="gao-muted">Evidence + match resolution only</div></div>
                    <button
                      type="button"
                      className="gao-toggle"
                      aria-pressed={hasDisputes}
                      disabled={busy === summary.participant.id}
                      onClick={() => setDisputeAccess(summary, !hasDisputes)}
                    >
                      {busy === summary.participant.id ? "Saving…" : hasDisputes ? "Enabled" : "Disabled"}
                    </button>
                  </div>
                </article>
              );
            })}
            {!limitedAdmins.length ? <div className="gao-empty">No limited admins have been invited yet.</div> : null}
          </div>
        </section>
      ) : null}

      {isFounder ? (
        <section className="gao-section">
          <div className="gao-section-head">
            <div>
              <div className="gao-eyebrow">SPONSOR RELATIONSHIPS • FOUNDER ONLY</div>
              <h2>Latest sponsor emails</h2>
            </div>
            <Link className="gao-refresh" to="/admin/sponsors">Open full inbox</Link>
          </div>
          <div className="gao-sponsors">
            {(sponsors?.messages || []).slice(0, 3).map((message) => (
              <Link className="gao-email" to={`/admin/sponsors?message=${encodeURIComponent(message.id)}`} key={message.id}>
                <div className="gao-email-from">{message.readAt ? "READ" : "● UNREAD"} • {message.fromName || message.fromEmail}</div>
                <h3>{message.subject || "No subject"}</h3>
                <p>{String(message.bodyText || "").slice(0, 180)}{String(message.bodyText || "").length > 180 ? "…" : ""}</p>
                <time>{dateTime(message.receivedAt || message.createdAt)}</time>
              </Link>
            ))}
            {!sponsors?.messages?.length ? <div className="gao-empty">No sponsor emails are in the inbox yet.</div> : null}
          </div>
        </section>
      ) : null}

      <section className="gao-section">
        <div className="gao-section-head">
          <div>
            <div className="gao-eyebrow">TOURNAMENT PROGRAM</div>
            <h2>{isFounder ? "All tournament operations" : "Tournament status"}</h2>
          </div>
          <Link className="gao-refresh" to="/tournaments">Open tournament hub</Link>
        </div>
        <div className="gao-tournament-list">
          {tournaments.map((tournament) => {
            const crewHq = isCrewHeadquartersTournament(tournament);
            const tournamentStatus = tournamentStatusById[tournament.id] || {};
            return (
              <article className={`gao-tournament ${crewHq ? "gao-tournament-hq" : ""}`} key={tournament.id}>
                <div>
                  <StatusPill tone={tournament.registrationOpen ? "green" : "gold"}>
                    {tournament.registrationOpen ? "REGISTRATION OPEN" : titleCase(tournament.status)}
                  </StatusPill>
                  <h3>{tournament.name}</h3>
                  <div className="gao-muted">{tournamentFormatLabel(tournament)}</div>
                  <div className="gao-muted">Date: {tournamentDateLabel(tournament)} • Prize: {money(tournament.prizeCents)}</div>
                  {crewHq ? (
                    <div className="gao-event-note">
                      Crew Headquarters event • captains choose the host HQ for normal rounds • G.A.S UP HQ available for non-crew teams • championship at G.A.S UP HQ.
                    </div>
                  ) : null}
                </div>
                <div className="gao-admin-stat"><span>APPROVED TEAMS</span><strong>{Number(tournament.approvedTeamCount || 0)}/{Number(tournament.maxTeams || 16)}</strong></div>
                <div className="gao-admin-stat"><span>SPOTS LEFT</span><strong>{Number(tournament.spotsRemaining ?? Math.max(0, Number(tournament.maxTeams || 16) - Number(tournament.approvedTeamCount || 0)))}</strong></div>
                <div className="gao-admin-stat"><span>OPEN DISPUTES</span><strong>{Number(tournamentStatus?.adminDisputeCount || 0)}</strong></div>
                <div className="gao-event-actions">
                  <Link
                    className="gao-event-action"
                    to={crewHq ? "/tournaments/gas-up-3v3-crew-headquarters" : "/tournaments"}
                  >
                    OPEN EVENT
                  </Link>
                  <Link
                    className="gao-event-action"
                    to={crewHq ? "/tournaments/gas-up-3v3-crew-headquarters/live" : "/tournaments/get-active-3v3-75-ovr/live"}
                  >
                    LIVE HUB
                  </Link>
                  <Link
                    className="gao-event-action"
                    to={`/admin/tournament-reviews?tournament=${encodeURIComponent(tournament.id)}`}
                  >
                    DISPUTES
                  </Link>
                  {isFounder ? (
                    <Link
                      className="gao-event-action"
                      to={`/admin/tournament-payouts?tournament=${encodeURIComponent(tournament.id)}`}
                    >
                      PAYOUTS
                    </Link>
                  ) : null}
                  {isFounder && crewHq ? (
                    <Link className="gao-event-action" to="/tournaments/gas-up-3v3-crew-headquarters#bot">
                      BOT TEST
                    </Link>
                  ) : null}
                </div>
              </article>
            );
          })}
          {!tournaments.length ? <div className="gao-empty">No tournaments are available right now.</div> : null}
        </div>
      </section>
    </main>
  );
}
